import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Edit2, Trash2, Eye, Plus, Calendar, Heart, FileText, X } from "lucide-react";
import { getMyPublications } from "../../API/getters";

export default function MyPublicationsPage() {
  const navigate = useNavigate();
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPublication, setSelectedPublication] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    const fetchMyPublications = async () => {
      try {
        setLoading(true);
        const data = await getMyPublications();
        setPublications(data);
      } catch (error) {
        console.error("Erro ao carregar publicações:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchMyPublications();
  }, []);

  const handleEdit = (publication) => {
    navigate(`/publicacoes/${publication.uuid}/editar`, {
      state: { publication },
    });
  };

  const handleView = (publication) => {
    navigate(`/feed/${publication.uuid}`, { state: { publication } });
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    setPublications(publications.filter((p) => p.uuid !== deleteId));
    setShowDeleteConfirm(false);
    setDeleteId(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Data desconhecida";
    const date = new Date(dateString);
    return date.toLocaleDateString("pt-PT", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div className="w-full py-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-row justify-between items-center">
          <div className="border-l-4 border-emerald-500 pl-4">
            <h1 className="text-3xl font-light text-gray-800 tracking-tight">
              Minhas Publicações
            </h1>
            <p className="text-gray-500 text-base font-light mt-1">
              Gere todas as suas publicações
            </p>
          </div>
          <button
            onClick={() => navigate("/publicar")}
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-lg text-sm font-medium transition-colors shadow-sm"
          >
            <Plus className="w-4 h-4" />
            Nova Publicação
          </button>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mb-3" />
          <span className="text-gray-400 text-sm">Carregando publicações...</span>
        </div>
      )}

      {/* Empty State */}
      {!loading && publications.length === 0 && (
        <div className="bg-gray-50/50 rounded-xl p-12 text-center border border-gray-100">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center">
              <FileText className="w-8 h-8 text-emerald-500" />
            </div>
          </div>
          <h3 className="text-lg font-light text-gray-700 mb-2">
            Nenhuma publicação
          </h3>
          <p className="text-gray-400 text-sm mb-6">
            Você ainda não criou nenhuma publicação
          </p>
          <button
            onClick={() => navigate("/publicar")}
            className="inline-flex items-center gap-2 bg-emerald-600 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Criar Primeira Publicação
          </button>
        </div>
      )}

      {/* Publications List */}
      {!loading && publications.length > 0 && (
        <div className="space-y-5">
          {publications.map((publication) => (
            <div
              key={publication.uuid}
              className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden"
            >
              {/* Publication Header */}
              <div className="p-5 border-b border-gray-100">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h2 className="text-lg font-medium text-gray-800 mb-1">
                      {publication.title}
                    </h2>
                    <div className="flex items-center gap-3 text-xs text-gray-400">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        <span>{formatDate(publication.created_at)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Heart className="w-3 h-3" />
                        <span>{publication.likes_count || 0} curtidas</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Publication Content */}
              <button
                className="w-full text-left px-5 py-4 hover:bg-gray-50/50 transition-colors"
                onClick={() => handleView(publication)}
              >
                <p className="text-gray-600 text-sm leading-relaxed line-clamp-3">
                  {publication.description}
                </p>
              </button>

              {/* Publication Image */}
              {publication.image && (
                <div className="px-5 pb-4">
                  <img
                    src={publication.image}
                    alt={publication.title}
                    className="w-full h-48 md:h-56 object-cover rounded-lg"
                  />
                </div>
              )}

              {/* Action Buttons */}
              <div className="border-t border-gray-100 flex justify-around py-3 px-5">
                <button
                  onClick={() => handleView(publication)}
                  className="flex items-center gap-2 text-gray-500 hover:text-emerald-600 transition-colors text-sm"
                >
                  <Eye className="w-4 h-4" />
                  <span>Ver</span>
                </button>
                <button
                  onClick={() => handleEdit(publication)}
                  className="flex items-center gap-2 text-gray-500 hover:text-emerald-600 transition-colors text-sm"
                >
                  <Edit2 className="w-4 h-4" />
                  <span>Editar</span>
                </button>
                <button
                  onClick={() => handleDeleteClick(publication.uuid)}
                  className="flex items-center gap-2 text-gray-500 hover:text-red-600 transition-colors text-sm"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Eliminar</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/50 backdrop-blur-sm" onClick={() => setShowDeleteConfirm(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-red-500" />
                </div>
                <h3 className="text-lg font-medium text-gray-800">
                  Eliminar Publicação?
                </h3>
              </div>
              <p className="text-gray-500 text-sm mb-6">
                Tem certeza que deseja eliminar esta publicação? Esta ação não pode ser desfeita.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 px-4 py-2 rounded-lg border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmDelete}
                  className="flex-1 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium transition-colors"
                >
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}